import { useContext, useEffect, useMemo, useState } from "react";
import {
  useCreate,
  useDelete,
  useOne,
  useNavigation,
  useUpdate,
} from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { AppImagePreview, AppTitle } from "components";
import { s3Path } from "helpers";
import { EnvContext } from "providers";
import { leagueMap, leagueOptions } from "pages/Video/leagueOptions";
import { RegulationModal } from "./RegulationModal";
import { PhotoModal } from "./PhotoModal";
import { WinnerModal } from "./WinnerModal";
import {
  MedicineContest,
  MedicineContestFormValues,
  MedicineContestPhoto,
  MedicineContestPhotoFormValues,
  MedicineContestRegulation,
  MedicineContestRegulationFormValues,
  MedicineContestWinner,
  MedicineContestWinnerFormValues,
  MedicineContestType,
} from "./types";

type MedicineContestFormPageProps = {
  contestType?: MedicineContestType;
  basePath?: string;
  createTitle?: string;
  editTitle?: string;
  entityName?: string;
  titlePlaceholder?: string;
};

export const MedicineContestFormPage = ({
  contestType = "CONTEST",
  basePath = "/medicine/contests",
  createTitle,
  editTitle,
  entityName,
  titlePlaceholder,
}: MedicineContestFormPageProps) => {
  const { THUMBOR_URL } = useContext(EnvContext);
  const { push } = useNavigation();
  const params = useParams();
  const itemName =
    entityName || (contestType === "CONFERENCE" ? "Конференция" : "Конкурс");
  const pageCreateTitle =
    createTitle ||
    (contestType === "CONFERENCE"
      ? "Создание конференции"
      : "Создание конкурса");
  const pageEditTitle =
    editTitle ||
    (contestType === "CONFERENCE"
      ? "Редактирование конференции"
      : "Редактирование конкурса");
  const placeholder =
    titlePlaceholder ||
    (contestType === "CONFERENCE"
      ? "Медицинская конференция"
      : "Медицинский конкурс");
  const routeYear = params.year ? Number(params.year) : undefined;
  const contestId = params.id ? Number(params.id) : undefined;
  const isEdit = Boolean(contestId);
  const [form] = Form.useForm<MedicineContestFormValues>();
  const watchedYear = Form.useWatch("year", form);
  const [currentContest, setCurrentContest] = useState<MedicineContest>();
  const [regulationModalOpen, setRegulationModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [editingRegulation, setEditingRegulation] =
    useState<MedicineContestRegulation>();
  const [editingPhoto, setEditingPhoto] = useState<MedicineContestPhoto>();
  const [editingWinner, setEditingWinner] = useState<MedicineContestWinner>();

  const { data, isLoading, refetch } = useOne<MedicineContest>({
    resource: "medicine-contests",
    dataProviderName: "medicineContestsDataProvider",
    id: contestId,
    meta: {
      contestType,
    },
    queryOptions: {
      enabled: isEdit && Boolean(contestId),
    },
  });
  const { mutate: createContest, isLoading: creatingContest } = useCreate();
  const { mutate: updateContest, isLoading: updatingContest } = useUpdate();
  const { mutate: createRegulation, isLoading: creatingRegulation } =
    useCreate();
  const { mutate: updateRegulation, isLoading: updatingRegulation } =
    useUpdate();
  const { mutate: createPhoto, isLoading: creatingPhoto } = useCreate();
  const { mutate: updatePhoto, isLoading: updatingPhoto } = useUpdate();
  const { mutate: createWinner, isLoading: creatingWinner } = useCreate();
  const { mutate: updateWinner, isLoading: updatingWinner } = useUpdate();
  const { mutate: deleteRegulation, isLoading: deletingRegulation } =
    useDelete();
  const { mutate: deletePhoto, isLoading: deletingPhoto } = useDelete();
  const { mutate: deleteWinner, isLoading: deletingWinner } = useDelete();

  const loadedContest = data?.data;
  const sortedWinners = useMemo(
    () =>
      [...(currentContest?.winners || [])].sort(
        (a, b) =>
          (a.leagueId ?? 0) - (b.leagueId ?? 0) ||
          (a.place ?? 0) - (b.place ?? 0)
      ),
    [currentContest?.winners]
  );
  const usedWinnerPlacesByLeague = useMemo(
    () =>
      (currentContest?.winners || []).reduce<Record<number, number[]>>(
        (acc, winner) => {
          if (!winner.leagueId) {
            return acc;
          }

          acc[winner.leagueId] = [
            ...(acc[winner.leagueId] || []),
            winner.place,
          ];
          return acc;
        },
        {}
      ),
    [currentContest?.winners]
  );
  const allWinnerPlacesFilled = leagueOptions.every(
    ({ value }) => (usedWinnerPlacesByLeague[value] || []).length >= 3
  );

  const getFileUrl = (fileUrl: string) => {
    if (/^https?:\/\//i.test(fileUrl)) {
      return fileUrl;
    }

    return s3Path({
      path: fileUrl,
      original: true,
      thumbor: THUMBOR_URL,
    });
  };

  useEffect(() => {
    if (loadedContest) {
      setCurrentContest(loadedContest);
      form.setFieldsValue({
        id: loadedContest.id,
        year: loadedContest.year,
        contestType,
        title: loadedContest.title,
        presentationsUrl: loadedContest.presentationsUrl,
        recordingUrl: loadedContest.recordingUrl,
      });
      return;
    }

    if (!isEdit) {
      form.setFieldsValue({
        year: routeYear,
        contestType,
      });
    }
  }, [contestType, form, isEdit, loadedContest, routeYear]);

  const closeRegulationModal = () => {
    setRegulationModalOpen(false);
    setEditingRegulation(undefined);
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setEditingPhoto(undefined);
  };

  const closeWinnerModal = () => {
    setWinnerModalOpen(false);
    setEditingWinner(undefined);
  };

  const updateCurrentContest = async (id: number, year: number) => {
    const result = await refetch();

    const nextContest = result.data?.data;

    if (nextContest) {
      setCurrentContest(nextContest);
      return;
    }

    if (!routeYear && year) {
      push(`${basePath}/${year}/edit/${id}`);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const basePayload: MedicineContestFormValues = {
      ...values,
      contestType,
    };
    const updateId = values.id || contestId;
    const payload = updateId ? { ...basePayload, id: updateId } : basePayload;
    const callbacks = {
      onSuccess: async (response: any) => {
        const savedContest = response?.data as MedicineContest | undefined;

        message.success(`${itemName} сохранен`);

        if (updateId) {
          await updateCurrentContest(updateId, values.year);
          return;
        }

        if (savedContest?.id) {
          setCurrentContest(savedContest);
          form.setFieldValue("id", savedContest.id);

          if (!isEdit) {
            push(`${basePath}/${savedContest.year}/edit/${savedContest.id}`);
          } else {
            await updateCurrentContest(savedContest.id, savedContest.year);
          }

          return;
        }

        message.error("Сервер не вернул ID созданной записи");
      },
      onError: (error: any) => {
        message.error(
          error?.message || `Не удалось сохранить ${itemName.toLowerCase()}`
        );
      },
    };

    if (updateId) {
      updateContest(
        {
          dataProviderName: "medicineContestsDataProvider",
          resource: "medicine-contests",
          id: updateId,
          values: payload,
        },
        callbacks
      );
      return;
    }

    createContest(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-contests",
        values: payload,
      },
      callbacks
    );
  };

  const handleRegulationSubmit = (
    values: MedicineContestRegulationFormValues
  ) => {
    const callbacks = {
      onSuccess: () => {
        message.success("Документ сохранен");
        closeRegulationModal();
        if (currentContest?.id && watchedYear) {
          updateCurrentContest(currentContest.id, watchedYear);
        }
      },
      onError: (error: any) => {
        message.error(error?.message || "Не удалось сохранить документ");
      },
    };
    const mutationParams = {
      dataProviderName: "medicineContestsDataProvider",
      resource: "medicine-contest-regulations",
      values,
    };

    if (values.id) {
      updateRegulation(
        {
          ...mutationParams,
          id: values.id,
        },
        callbacks
      );
      return;
    }

    createRegulation(mutationParams, callbacks);
  };

  const handleRegulationDelete = (regulation: MedicineContestRegulation) => {
    deleteRegulation(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-contest-regulations",
        id: regulation.id,
      },
      {
        onSuccess: () => {
          message.success("Документ удален");
          if (currentContest?.id && watchedYear) {
            updateCurrentContest(currentContest.id, watchedYear);
          }
        },
        onError: (error: any) => {
          message.error(error?.message || "Не удалось удалить документ");
        },
      }
    );
  };

  const handlePhotoSubmit = (values: MedicineContestPhotoFormValues) => {
    const callbacks = {
      onSuccess: () => {
        message.success("Фото сохранено");
        closePhotoModal();
        if (currentContest?.id && watchedYear) {
          updateCurrentContest(currentContest.id, watchedYear);
        }
      },
      onError: (error: any) => {
        message.error(error?.message || "Не удалось сохранить фото");
      },
    };
    const mutationParams = {
      dataProviderName: "medicineContestsDataProvider",
      resource: "medicine-contest-photos",
      values,
    };

    if (values.id) {
      updatePhoto(
        {
          ...mutationParams,
          id: values.id,
        },
        callbacks
      );
      return;
    }

    createPhoto(mutationParams, callbacks);
  };

  const handlePhotoDelete = (photo: MedicineContestPhoto) => {
    deletePhoto(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-contest-photos",
        id: photo.id,
      },
      {
        onSuccess: () => {
          message.success("Фото удалено");
          if (currentContest?.id && watchedYear) {
            updateCurrentContest(currentContest.id, watchedYear);
          }
        },
        onError: (error: any) => {
          message.error(error?.message || "Не удалось удалить фото");
        },
      }
    );
  };

  const handleWinnerSubmit = (values: MedicineContestWinnerFormValues) => {
    const callbacks = {
      onSuccess: () => {
        message.success("Победитель сохранен");
        closeWinnerModal();
        if (currentContest?.id && watchedYear) {
          updateCurrentContest(currentContest.id, watchedYear);
        }
      },
      onError: (error: any) => {
        // error.message может быть объектом тела ошибки Spring — берём строку.
        const reason =
          typeof error?.message === "string" ? error.message : undefined;
        message.error(reason || "Не удалось сохранить победителя");
      },
    };
    const mutationParams = {
      dataProviderName: "medicineContestsDataProvider",
      resource: "medicine-contest-winners",
      values,
      // Гасим стандартную нотификацию Refine — иначе она попытается
      // отрендерить объект ошибки и уронит страницу.
      errorNotification: false as const,
    };

    if (values.id) {
      updateWinner(
        {
          ...mutationParams,
          id: values.id,
        },
        callbacks
      );
      return;
    }

    createWinner(mutationParams, callbacks);
  };

  const handleWinnerDelete = (winner: MedicineContestWinner) => {
    deleteWinner(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-contest-winners",
        id: winner.id,
      },
      {
        onSuccess: () => {
          message.success("Победитель удален");
          if (currentContest?.id && watchedYear) {
            updateCurrentContest(currentContest.id, watchedYear);
          }
        },
        onError: (error: any) => {
          message.error(error?.message || "Не удалось удалить победителя");
        },
      }
    );
  };

  return (
    <Show
      isLoading={isEdit && isLoading}
      title={
        <AppTitle canGoBack>
          {isEdit ? pageEditTitle : pageCreateTitle}
        </AppTitle>
      }
      breadcrumb={false}
      headerButtons={<></>}
      footerButtons={null}
      goBack={false}
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <Form<MedicineContestFormValues> form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="contestType" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="year"
            label="Год"
            rules={[
              {
                required: true,
                message: "Укажите год",
              },
            ]}
          >
            <InputNumber min={2000} max={2100} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="title"
            label="Название"
            rules={[
              {
                required: true,
                message: "Введите название",
              },
            ]}
          >
            <Input placeholder={placeholder} />
          </Form.Item>

          <Form.Item name="presentationsUrl" label="Ссылка на презентации">
            <Input placeholder="https://disk.yandex.ru/..." />
          </Form.Item>

          {contestType === "CONFERENCE" && (
            <Form.Item name="recordingUrl" label="Ссылка на запись">
              <Input placeholder="/upload/medicine/conference/2026-recording.mp4" />
            </Form.Item>
          )}

          <Button
            type="primary"
            loading={creatingContest || updatingContest}
            onClick={handleSubmit}
          >
            Сохранить
          </Button>
        </Form>

        <Table<MedicineContestRegulation>
          dataSource={currentContest?.regulations || []}
          rowKey="id"
          pagination={false}
          title={() => (
            <Space>
              <Typography.Text strong>Документы</Typography.Text>
              <Button
                size="small"
                icon={<PlusOutlined />}
                disabled={!currentContest?.id}
                onClick={() => {
                  setEditingRegulation(undefined);
                  setRegulationModalOpen(true);
                }}
              >
                Добавить документ
              </Button>
            </Space>
          )}
          locale={{
            emptyText: (
              <div style={{ margin: 24 }}>
                {currentContest?.id
                  ? "Документы отсутствуют"
                  : "Сохраните конкурс, чтобы добавить документы"}
              </div>
            ),
          }}
        >
          <Table.Column<MedicineContestRegulation>
            dataIndex="title"
            key="title"
            title="Название"
          />
          <Table.Column<MedicineContestRegulation>
            dataIndex="leagueId"
            key="leagueId"
            title="Лига"
            render={(value) => leagueMap[value] || value}
          />
          <Table.Column<MedicineContestRegulation>
            dataIndex="fileUrl"
            key="fileUrl"
            title="Файл"
            render={(value) => (
              <Typography.Link
                href={getFileUrl(value)}
                target="_blank"
                rel="noreferrer"
              >
                {value?.replace(/.*\//, "")}
              </Typography.Link>
            )}
          />
          <Table.Column<MedicineContestRegulation>
            width={220}
            fixed="right"
            key="actions"
            title="Действия"
            render={(_, record) => (
              <Space size={8} direction="vertical" style={{ width: "100%" }}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingRegulation(record);
                    setRegulationModalOpen(true);
                  }}
                  block
                >
                  Редактировать
                </Button>
                <Popconfirm
                  title="Удалить документ?"
                  okText="Удалить"
                  cancelText="Отмена"
                  onConfirm={() => handleRegulationDelete(record)}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={deletingRegulation}
                    block
                  >
                    Удалить
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>

        {contestType !== "CONFERENCE" && (
          <Table<MedicineContestWinner>
            dataSource={sortedWinners}
            rowKey="id"
            pagination={false}
            title={() => {
              return (
                <Space>
                  <Typography.Text strong>Победители</Typography.Text>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    disabled={!currentContest?.id || allWinnerPlacesFilled}
                    onClick={() => {
                      setEditingWinner(undefined);
                      setWinnerModalOpen(true);
                    }}
                  >
                    Добавить победителя
                  </Button>
                </Space>
              );
            }}
            locale={{
              emptyText: (
                <div style={{ margin: 24 }}>
                  {currentContest?.id
                    ? "Победители отсутствуют"
                    : "Сохраните конкурс, чтобы добавить победителей"}
                </div>
              ),
            }}
          >
            <Table.Column<MedicineContestWinner>
              width={140}
              dataIndex="leagueId"
              key="leagueId"
              title="Лига"
              render={(value) => leagueMap[value] || value || "-"}
            />
            <Table.Column<MedicineContestWinner>
              width={100}
              dataIndex="place"
              key="place"
              title="Место"
              render={(value) => `${value} место`}
            />
            <Table.Column<MedicineContestWinner>
              dataIndex="clubName"
              key="clubName"
              title="Команда"
              render={(value, record) => value || record.teamSlug || "-"}
            />
            <Table.Column<MedicineContestWinner>
              width={180}
              fixed="right"
              key="actions"
              title="Действия"
              render={(_, record) => (
                <Space size={8} direction="vertical" style={{ width: "100%" }}>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingWinner(record);
                      setWinnerModalOpen(true);
                    }}
                    block
                  >
                    Редактировать
                  </Button>
                  <Popconfirm
                    title="Удалить победителя?"
                    okText="Удалить"
                    cancelText="Отмена"
                    onConfirm={() => handleWinnerDelete(record)}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deletingWinner}
                      block
                    >
                      Удалить
                    </Button>
                  </Popconfirm>
                </Space>
              )}
            />
          </Table>
        )}

        <Table<MedicineContestPhoto>
          dataSource={currentContest?.photos || []}
          rowKey="id"
          pagination={false}
          title={() => (
            <Space>
              <Typography.Text strong>Фото</Typography.Text>
              <Button
                size="small"
                icon={<PlusOutlined />}
                disabled={!currentContest?.id}
                onClick={() => {
                  setEditingPhoto(undefined);
                  setPhotoModalOpen(true);
                }}
              >
                Добавить фото
              </Button>
            </Space>
          )}
          locale={{
            emptyText: (
              <div style={{ margin: 24 }}>
                {currentContest?.id
                  ? "Фото отсутствуют"
                  : "Сохраните конкурс, чтобы добавить фото"}
              </div>
            ),
          }}
        >
          <Table.Column<MedicineContestPhoto>
            width={180}
            dataIndex="photoUrl"
            key="photoUrl"
            title="Фото"
            render={(value) => (
              <AppImagePreview
                imageURL={value}
                useS3={!/^https?:\/\//i.test(value)}
              />
            )}
          />
          <Table.Column<MedicineContestPhoto>
            width={180}
            fixed="right"
            key="actions"
            title="Действия"
            render={(_, record) => (
              <Space size={8} direction="vertical" style={{ width: "100%" }}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingPhoto(record);
                    setPhotoModalOpen(true);
                  }}
                  block
                >
                  Редактировать
                </Button>
                <Popconfirm
                  title="Удалить фото?"
                  okText="Удалить"
                  cancelText="Отмена"
                  onConfirm={() => handlePhotoDelete(record)}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={deletingPhoto}
                    block
                  >
                    Удалить
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </Space>

      {currentContest?.id && (
        <RegulationModal
          open={regulationModalOpen}
          contestId={currentContest.id}
          saving={creatingRegulation || updatingRegulation}
          regulation={editingRegulation}
          onCancel={closeRegulationModal}
          onSubmit={handleRegulationSubmit}
        />
      )}

      {currentContest?.id && (
        <PhotoModal
          open={photoModalOpen}
          contestId={currentContest.id}
          saving={creatingPhoto || updatingPhoto}
          photo={editingPhoto}
          onCancel={closePhotoModal}
          onSubmit={handlePhotoSubmit}
        />
      )}

      {currentContest?.id && contestType !== "CONFERENCE" && (
        <WinnerModal
          open={winnerModalOpen}
          contestId={currentContest.id}
          saving={creatingWinner || updatingWinner}
          winner={editingWinner}
          usedPlacesByLeague={usedWinnerPlacesByLeague}
          onCancel={closeWinnerModal}
          onSubmit={handleWinnerSubmit}
        />
      )}
    </Show>
  );
};
